import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { i18n } from '../i18n/i18n'
import labelApprovalApi, {
    APPROVAL_TEXT_MAX_LENGTH,
    LabelApprovalPublicData,
    labelDisplaySize,
    labelSizeText
} from '../apis/labelApproval.api'
import AuthCard from '../components/AuthCard'
import Button from '../components/Button'
import Page from '../components/Page'

interface MatchParams {
    token: string
}

interface Props extends RouteComponentProps<MatchParams> {}

interface State {
    approval: LabelApprovalPublicData | null
    isLoading: boolean
    isSubmitting: boolean
    error: string
    remarks: string
    decision: boolean | null
}

/**
 * Public page an approver lands on from the emailed link. No sign-in: the token
 * in the URL is the credential.
 *
 * The email's Yes/No buttons only preselect the decision here — the approval is
 * never recorded by opening a link, because mail scanners follow links on the
 * recipient's behalf and would silently approve labels.
 */
class LabelApproval extends Component<Props, State> {
    state: State = {
        approval: null,
        isLoading: true,
        isSubmitting: false,
        error: '',
        remarks: '',
        decision: null
    }

    get token () {
        return this.props.match.params.token
    }

    get presetDecision (): boolean | null {
        const decision = new URLSearchParams(this.props.location.search).get('decision')
        if (decision === 'yes') return true
        if (decision === 'no') return false
        return null
    }

    errorMessage (error: any) {
        const { response } = error || {}
        return (
            (response && response.data && response.data.message) ||
            i18n('This approval link is not valid.')
        )
    }

    componentDidMount () {
        labelApprovalApi.show(
            this.token,
            approval => {
                this.setState({
                    approval,
                    isLoading: false,
                    decision: approval && approval.isAnswerable
                        ? this.presetDecision
                        : null
                })
            },
            error => {
                this.setState({
                    isLoading: false,
                    error: this.errorMessage(error)
                })
            }
        )
    }

    handleRemarksChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            remarks: event.target.value.slice(0, this.remarksMaxLength)
        })
    }

    get remarksMaxLength () {
        const { approval } = this.state
        return (approval && approval.remarksMaxLength) || APPROVAL_TEXT_MAX_LENGTH
    }

    handleDecision = (approved: boolean) => {
        this.setState({ decision: approved })
    }

    handleSubmit = () => {
        const { decision, remarks, isSubmitting } = this.state
        if (decision === null || isSubmitting) return
        this.setState({ isSubmitting: true })
        labelApprovalApi.respond(
            this.token,
            { approved: decision, remarks: remarks.trim() || undefined },
            approval => {
                this.setState({ approval, isSubmitting: false, error: '' })
            },
            error => {
                this.setState({
                    isSubmitting: false,
                    error: this.errorMessage(error)
                })
            }
        )
    }

    /**
     * Rendered at the design's own proportions and at a size derived from its
     * real dimensions, so the approver judges the label at the shape and scale
     * it will actually print — matching what the email shows.
     */
    get labelImage () {
        const { approval } = this.state
        if (!approval || !approval.snapshotUrl) return null
        const size = labelDisplaySize(
            approval.labelWidth,
            approval.labelHeight,
            approval.labelUnit
        )
        const sizeText = labelSizeText(
            approval.labelWidth,
            approval.labelHeight,
            approval.labelUnit
        )
        return (
            <div className="mb-5">
                {/* height stays auto so a narrow screen scales the image down
                    instead of squashing it out of shape */}
                <div className="border border-solid border-gray-200 rounded-lg bg-white p-3 flex justify-center">
                    <img
                        className="rounded-lg"
                        src={approval.snapshotUrl}
                        alt={approval.labelName}
                        style={size
                            ? { width: size.width, maxWidth: '100%', height: 'auto' }
                            : { maxWidth: '100%', maxHeight: 360 }}/>
                </div>
                {sizeText && (
                    <p className="text-xs text-secondary mt-1">
                        {i18n('Label size')}: {sizeText}
                    </p>
                )}
            </div>
        )
    }

    get contactLine () {
        return (
            <p className="text-xs text-secondary mt-5">
                {i18n('If you have any questions, please contact')}&nbsp;
                <a className="text-blue-700" href="mailto:hello@quandopro.com">
                    hello@quandopro.com
                </a>
            </p>
        )
    }

    /**
     * Every state renders inside one white card on the tinted page, matching
     * the sign-in screen — this is a public page, so it should look like the
     * rest of the signed-out experience rather than a bare document.
     */
    shell (heading: string, description: string, body: React.ReactNode) {
        return (
            <Page size="half" wrapperClass="pt-20">
                <AuthCard heading={heading} description={description}>
                    {body}
                </AuthCard>
            </Page>
        )
    }

    renderLoading () {
        return this.shell(
            i18n('Label approval request'),
            i18n('Loading...'),
            null
        )
    }

    renderError () {
        return this.shell(
            i18n('Approval link unavailable'),
            this.state.error,
            this.contactLine
        )
    }

    /**
     * Shown once the request has been answered — by this visit or an earlier one.
     */
    renderAnswered () {
        const { approval } = this.state
        if (!approval) return null
        const isApproved = approval.status === 'approved'
        const isRejected = approval.status === 'rejected'
        const heading = isApproved
            ? i18n('Label approved')
            : isRejected
                ? i18n('Label not approved')
                : i18n('This request is closed')
        const description = isApproved
            ? i18n('Thank you. Your approval has been recorded and the requester has been notified.')
            : isRejected
                ? i18n('Thank you. Your response has been recorded and the requester has been notified.')
                : i18n('This approval request is no longer open.')
        return this.shell(heading, description, (
            <>
                {this.labelImage}
                <p className="text-sm mb-1">
                    <strong>{approval.labelName}</strong>
                </p>
                {approval.responderRemarks && (
                    <p className="text-sm text-secondary">
                        {i18n('Your remarks')}: {approval.responderRemarks}
                    </p>
                )}
                {this.contactLine}
            </>
        ))
    }

    renderForm () {
        const { approval, remarks, decision, isSubmitting } = this.state
        if (!approval) return null
        const remaining = this.remarksMaxLength - remarks.length
        const decisionClass = (active: boolean) => {
            const base = 'py-2 px-5 text-white capitalize focus:outline-none'
            const tone = active
                ? (decision === true ? 'bg-green-600' : 'bg-red-600')
                : 'bg-gray-400'
            return `${base} ${tone} ${isSubmitting ? 'opacity-60' : ''}`
        }
        const description = approval.requestedBy
            ? `${approval.requestedBy} ${i18n('has asked you to review the label')} ${approval.labelName}.`
            : `${i18n('you have been asked to review the label')} ${approval.labelName}.`

        return this.shell(
            `${i18n('Hello')} ${approval.approverName}`,
            description,
            <>
                {approval.message && (
                    <p className="mb-4 px-4 py-3 bg-gray-50 border-l-4 border-primary text-sm break-words">
                        {approval.message}
                    </p>
                )}

                {this.labelImage}

                <p className="mb-2 text-sm">{i18n('Do you approve this label?')}</p>
                <div className="flex gap-3 mb-4">
                    <button
                        type="button"
                        className={decisionClass(decision === true)}
                        disabled={isSubmitting}
                        onClick={() => this.handleDecision(true)}>
                        {i18n('Yes, approve')}
                    </button>
                    <button
                        type="button"
                        className={decisionClass(decision === false)}
                        disabled={isSubmitting}
                        onClick={() => this.handleDecision(false)}>
                        {i18n('No, Not Approved')}
                    </button>
                </div>

                <label className="block text-sm text-secondary mb-1">
                    {i18n('Remarks (optional)')}
                </label>
                {/*
                  * `input-default` pins the height to a single line (h-input),
                  * so a textarea needs it released.
                  */}
                <textarea
                    className="input-default"
                    style={{ height: 'auto', minHeight: 72, paddingTop: 8, paddingBottom: 8 }}
                    rows={3}
                    maxLength={this.remarksMaxLength}
                    value={remarks}
                    disabled={isSubmitting}
                    onChange={this.handleRemarksChange}/>
                <p className="text-xs text-secondary mt-1 mb-4">
                    {remaining} {i18n('characters remaining')}
                </p>

                {this.state.error && (
                    <p className="text-red-600 text-sm mb-3">{this.state.error}</p>
                )}

                <Button
                    text={isSubmitting ? i18n('Submitting...') : i18n('Submit response')}
                    progress={isSubmitting}
                    disabled={decision === null}
                    onClick={this.handleSubmit}/>

                <p className="text-xs text-secondary mt-3">
                    {i18n('Your answer is recorded once and cannot be changed afterwards.')}
                </p>
            </>
        )
    }

    render () {
        const { isLoading, error, approval } = this.state
        if (isLoading) return this.renderLoading()
        if (!approval) return this.renderError()
        if (!approval.isAnswerable) return this.renderAnswered()
        return this.renderForm()
    }
}

export default withRouter(LabelApproval)
